import { useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Printer,
  Package,
  Receipt,
  DollarSign,
  X,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { formatDate } from "@/utils/dateUtils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import {
  DoctorOrPracticeOwner,
} from "@/components/auth/RoleGuard";
import { sendPrintRequest } from "@/utils/printUtils";
import { showSuccess, showError, showWarning } from "@/utils/toastHelpers";
import PrintablePrescription from "./PrintablePrescription";
import PrescriptionInvoicesModal from "./PrescriptionInvoicesModal";
import { billingService } from "@/api/services";

const dispenseStatusColors = {
  pending: "bg-yellow-100 text-yellow-700 border-yellow-200",
  partially_dispensed: "bg-blue-100 text-blue-700 border-blue-200",
  fully_dispensed: "bg-green-100 text-green-700 border-green-200",
  external: "bg-purple-100 text-purple-700 border-purple-200",
};

export default function PrescriptionDetail({
  prescription,
  doctors = [],
  onClose,
  onEdit,
  onDelete,
  onDispense,
  isPatientView = false,
}) {
  const queryClient = useQueryClient();
  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [showGenerateInvoiceModal, setShowGenerateInvoiceModal] =
    useState(false);
  const [selectedDispenses, setSelectedDispenses] = useState([]);
  const [showLabelPrintModal, setShowLabelPrintModal] = useState(false);
  const [selectedMedications, setSelectedMedications] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);

  // Fetch associated clinical note if visit_id exists
  const { data: associatedClinicalNote } = useQuery({
    queryKey: ["clinical-note", prescription.visit_id],
    queryFn: () =>
      apiClient.get(ENDPOINTS.CLINICAL_NOTES.GET(prescription.visit_id)),
    enabled: !!prescription.visit_id,
  });

  // Fetch invoices for this prescription
  const { data: invoices = [], isLoading: isLoadingInvoices } = useQuery({
    queryKey: ["prescription-invoices", prescription.id],
    queryFn: () =>
      apiClient.get(ENDPOINTS.PRESCRIPTIONS.INVOICES(prescription.id)),
    enabled: showInvoicesModal,
  });

  // Fetch uninvoiced dispenses
  const { data: uninvoicedDispenses = [], isLoading: isLoadingDispenses } =
    useQuery({
      queryKey: ["uninvoiced-dispenses", prescription.id],
      queryFn: () =>
        apiClient.get(
          ENDPOINTS.PRESCRIPTIONS.UNINVOICED_DISPENSES(prescription.id)
        ),
      enabled: showGenerateInvoiceModal,
    });

  // Fetch Xero connection status (required for invoice generation)
  const { data: xeroStatus } = useQuery({
    queryKey: ["xeroStatus"],
    queryFn: billingService.getXeroStatus,
  });

  // Generate invoice mutation
  const generateInvoice = useMutation({
    mutationFn: (dispenses) =>
      apiClient.post(
        ENDPOINTS.PRESCRIPTIONS.GENERATE_INVOICE(prescription.id),
        { dispenses }
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["prescriptions"]);
      queryClient.invalidateQueries(["invoices"]);
      queryClient.invalidateQueries(["uninvoiced-dispenses"]);
      setShowGenerateInvoiceModal(false);
      setSelectedDispenses([]);
      showSuccess(
        "Invoice created successfully",
        `Invoice #${data.xeroInvoiceNumber || data.invoiceId} - Total: R${data.total.toFixed(2)}`
      );
    },
    onError: (error) => {
      // Extract error details - handle both direct error objects and response-wrapped errors
      const errorData = error?.response?.data || error?.data || error;
      const errorMessage =
        errorData?.error ||
        errorData?.message ||
        error.message ||
        "Unknown error";

      showError("Failed to generate invoice", errorMessage);
    },
  });

  const handlePrint = () => {
    // This is a browser-native print dialog
    window.print();
  };

  const handleGenerateInvoice = () => {
    setShowGenerateInvoiceModal(true);
  };

  const handleToggleDispense = (dispense) => {
    setSelectedDispenses((prev) => {
      const key = `${dispense.medicationId}-${dispense.dispenseIndex}`;
      const isSelected = prev.some(
        (d) => `${d.medicationId}-${d.dispenseIndex}` === key
      );

      if (isSelected) {
        return prev.filter(
          (d) => `${d.medicationId}-${d.dispenseIndex}` !== key
        );
      } else {
        return [
          ...prev,
          {
            medicationId: dispense.medicationId,
            dispenseIndex: dispense.dispenseIndex,
          },
        ];
      }
    });
  };

  const handleSubmitInvoice = () => {
    if (selectedDispenses.length === 0) {
      showWarning("Please select at least one dispense event to invoice");
      return;
    }
    generateInvoice.mutate(selectedDispenses);
  };

  // Label printing functions
  const handleToggleMedication = (medication, index) => {
    setSelectedMedications((prev) => {
      const isSelected = prev.some((m) => m.index === index);

      if (isSelected) {
        return prev.filter((m) => m.index !== index);
      } else {
        return [...prev, { ...medication, index }];
      }
    });
  };

  const handlePrintLabels = async () => {
    if (selectedMedications.length === 0) {
      showWarning("Please select at least one medication to print labels for");
      return;
    }

    setIsPrinting(true);

    const errors = [];
    let successCount = 0;

    try {
      for (const medication of selectedMedications) {
        try {
          await sendPrintRequest(medication, prescription);
          successCount++;
        } catch (error) {
          errors.push(`${medication.medicationName}: ${error.message}`);
        }
      }
    } catch (error) {
      errors.push(`Unexpected error: ${error.message}`);
    }

    setIsPrinting(false);

    // Show toasts based on results
    if (errors.length === 0) {
      showSuccess(
        "Labels sent to printer",
        `Successfully sent ${successCount} label(s)`
      );
      setShowLabelPrintModal(false);
      setSelectedMedications([]);
    } else {
      if (successCount > 0) {
        showWarning(
          "Partial success",
          `Printed ${successCount} of ${selectedMedications.length} label(s). ${errors.length} failed.`
        );
      } else {
        showError("Failed to print labels", errors.join(", "));
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between print:hidden">
        <Button variant="ghost" onClick={onClose} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to List
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint} className="gap-2">
            <Printer className="w-4 h-4" />
            Print
          </Button>
          <DoctorOrPracticeOwner>
            <Button
              variant="outline"
              onClick={() => setShowLabelPrintModal(true)}
              className="gap-2 bg-purple-50 hover:bg-purple-100 border-purple-200"
            >
              <Printer className="w-4 h-4" />
              Print Labels
            </Button>
            {onDispense && (
              <Button
                variant="outline"
                onClick={() => onDispense(prescription)}
                className="gap-2 bg-blue-50 hover:bg-blue-100 border-blue-200"
              >
                <Package className="w-4 h-4" />
                Dispense
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleGenerateInvoice}
              disabled={generateInvoice.isPending || !xeroStatus?.connected}
              className="gap-2 bg-green-50 hover:bg-green-100 border-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title={!xeroStatus?.connected ? "Xero must be connected to generate invoices" : ""}
            >
              <Receipt className="w-4 h-4" />
              {generateInvoice.isPending ? "Generating..." : "Generate Invoice"}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowInvoicesModal(true)}
              className="gap-2"
            >
              <DollarSign className="w-4 h-4" />
              View Invoices
            </Button>
            {onEdit && (
              <Button
                variant="outline"
                onClick={() => onEdit(prescription)}
                className="gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit
              </Button>
            )}
            {onDelete && (
              <Button
                variant="destructive"
                onClick={() => onDelete(prescription.id)}
                className="gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </Button>
            )}
          </DoctorOrPracticeOwner>
        </div>
      </div>

      {/* Xero Connection Warning */}
      <DoctorOrPracticeOwner>
        {!xeroStatus?.connected && (
          <div className="mx-6 mt-4 mb-2 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-amber-900 mb-1">
                Xero Not Connected
              </h4>
              <p className="text-sm text-amber-700">
                Invoice generation requires an active Xero connection. Please connect to Xero from the Billing page to create invoices.
              </p>
            </div>
          </div>
        )}
      </DoctorOrPracticeOwner>

      <PrintablePrescription
        prescription={prescription}
        doctors={doctors}
        associatedClinicalNote={associatedClinicalNote}
      />

      {/* Generate Invoice Modal */}
      {showGenerateInvoiceModal &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                    <Receipt className="w-6 h-6 text-green-600" />
                    Generate Invoice
                  </h2>
                  <p className="text-sm text-slate-600 mt-1">
                    Select dispense events to include in the invoice
                  </p>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowGenerateInvoiceModal(false);
                    setSelectedDispenses([]);
                  }}
                  className="gap-2"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {isLoadingDispenses ? (
                  <div className="flex items-center justify-center py-12">
                    <p className="text-slate-600">Loading dispenses...</p>
                  </div>
                ) : uninvoicedDispenses.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Package className="w-16 h-16 text-slate-300 mb-4" />
                    <p className="text-slate-600 text-lg">
                      No uninvoiced dispenses
                    </p>
                    <p className="text-slate-500 text-sm mt-2">
                      All dispenses for this prescription have been invoiced
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-sm text-slate-600 mb-4">
                      {selectedDispenses.length} of {uninvoicedDispenses.length}{" "}
                      dispense
                      {uninvoicedDispenses.length !== 1 ? "s" : ""} selected
                    </div>
                    {uninvoicedDispenses.map((dispense, idx) => {
                      const isSelected = selectedDispenses.some(
                        (d) =>
                          `${d.medicationId}-${d.dispenseIndex}` ===
                          `${dispense.medicationId}-${dispense.dispenseIndex}`
                      );
                      return (
                        <div
                          key={idx}
                          onClick={() => handleToggleDispense(dispense)}
                          className={`border rounded-lg p-4 cursor-pointer transition-all ${
                            isSelected
                              ? "border-green-500 bg-green-50"
                              : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Checkbox */}
                            <div className="mt-1">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                className="w-5 h-5 text-green-600 rounded cursor-pointer"
                              />
                            </div>

                            {/* Dispense Info */}
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h3 className="font-semibold text-slate-900">
                                    {dispense.medicationName}
                                  </h3>
                                  <p className="text-sm text-slate-600 mt-1">
                                    Dispensed:{" "}
                                    {formatDate(
                                      dispense.dispensedAt,
                                      "MMM d, yyyy"
                                    )}
                                  </p>
                                  {dispense.batchNumber && (
                                    <p className="text-xs text-slate-500 mt-1">
                                      Batch: {dispense.batchNumber}
                                    </p>
                                  )}
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-slate-900">
                                    R{dispense.lineAmount.toFixed(2)}
                                  </p>
                                  <p className="text-sm text-slate-600">
                                    {dispense.quantity} x R
                                    {dispense.sellingPrice.toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="border-t p-4 bg-slate-50">
                <div className="flex items-center justify-between">
                  <div>
                    {selectedDispenses.length > 0 && (
                      <p className="text-sm font-semibold text-slate-900">
                        Total: R
                        {uninvoicedDispenses
                          .filter((d) =>
                            selectedDispenses.some(
                              (s) =>
                                `${s.medicationId}-${s.dispenseIndex}` ===
                                `${d.medicationId}-${d.dispenseIndex}`
                            )
                          )
                          .reduce((sum, d) => sum + d.lineAmount, 0)
                          .toFixed(2)}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowGenerateInvoiceModal(false);
                        setSelectedDispenses([]);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSubmitInvoice}
                      disabled={
                        selectedDispenses.length === 0 ||
                        generateInvoice.isPending
                      }
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {generateInvoice.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Receipt className="w-4 h-4 mr-2" />
                          Create Invoice
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      <PrescriptionInvoicesModal
        isOpen={showInvoicesModal}
        onClose={() => setShowInvoicesModal(false)}
        invoices={invoices}
        isLoading={isLoadingInvoices}
      />

      {/* Label Print Modal */}
      {showLabelPrintModal &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                    <Printer className="w-6 h-6 text-purple-600" />
                    Print Medication Labels
                  </h2>
                  <p className="text-sm text-slate-600 mt-1">
                    Select which medications to print labels for
                  </p>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowLabelPrintModal(false);
                    setSelectedMedications([]);
                  }}
                  className="gap-2"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {!prescription.medications ||
                prescription.medications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Package className="w-16 h-16 text-slate-300 mb-4" />
                    <p className="text-slate-600 text-lg">
                      No medications on this prescription
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-sm text-slate-600 mb-4">
                      {selectedMedications.length} of{" "}
                      {prescription.medications.length} medication
                      {prescription.medications.length !== 1 ? "s" : ""}{" "}
                      selected
                    </div>
                    {prescription.medications.map((medication, index) => {
                      const isSelected = selectedMedications.some(
                        (m) => m.index === index
                      );
                      return (
                        <div
                          key={index}
                          onClick={() =>
                            handleToggleMedication(medication, index)
                          }
                          className={`border rounded-lg p-4 cursor-pointer transition-all ${
                            isSelected
                              ? "border-purple-500 bg-purple-50"
                              : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Checkbox */}
                            <div className="mt-1">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                className="w-5 h-5 text-purple-600 rounded cursor-pointer"
                              />
                            </div>

                            {/* Medication Info */}
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h3 className="font-semibold text-slate-900 text-lg">
                                    {medication.medicationName}
                                  </h3>
                                  <p className="text-slate-700 mt-1">
                                    <span className="font-medium">Dosage:</span>{" "}
                                    {medication.dosage}
                                  </p>
                                  <p className="text-sm text-slate-600 mt-2">
                                    {medication.instructions}
                                  </p>
                                  <div className="flex gap-4 text-sm text-slate-600 mt-2">
                                    {medication.quantity && (
                                      <span>
                                        <strong>Packs/fill:</strong>{" "}
                                        {medication.quantity}
                                      </span>
                                    )}
                                    <span>
                                      <strong>Freq:</strong>{" "}
                                      {medication.frequency}
                                    </span>
                                    <span>
                                      <strong>Duration:</strong>{" "}
                                      {medication.duration}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="border-t p-4 bg-slate-50">
                <div className="flex items-center justify-between">
                  <div>
                    {selectedMedications.length > 0 && (
                      <p className="text-sm font-semibold text-slate-900">
                        {selectedMedications.length} label
                        {selectedMedications.length !== 1 ? "s" : ""} selected
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowLabelPrintModal(false);
                        setSelectedMedications([]);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handlePrintLabels}
                      disabled={selectedMedications.length === 0 || isPrinting}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {isPrinting ? "Printing..." : "Print Labels"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .printable-area,
          .printable-area * {
            visibility: visible;
          }
          .printable-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            border: none;
            box-shadow: none;
          }
        }
      `}</style>
    </div>
  );
}
